import fs from 'fs'
import path from 'path'

import type { Answers, Template } from './manifest'
import { serviceDirectory as resolveServiceDirectory, servicesDirectory } from './paths'
import { render } from './render'

// Files whose bytes must survive untouched. Everything else is rendered as UTF-8 text — a template is
// overwhelmingly source, and an icon is the one thing likely to be binary.
const BINARY_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.ico', '.zip', '.woff', '.woff2'])

export interface RenderedFile {
  /** Path relative to the service directory, e.g. `src/index.js`. */
  relativePath: string
  contents: Buffer
}

export interface ScaffoldResult {
  serviceDirectory: string
  files: RenderedFile[]
  createdServicesDirectory: boolean
}

/**
 * Render every file the template declares, in memory.
 *
 * Nothing touches disk here: a bad placeholder or an unknown flag must fail before the first `mkdir`, or
 * the user is left cleaning up a half-written service directory by hand.
 */
export function renderTemplate(template: Template, answers: Answers): RenderedFile[] {
  const filesRoot = path.join(template.directory, 'files')
  const rendered: RenderedFile[] = []

  for (const file of template.manifest.files) {
    if (file.when && !answers[file.when]) {
      continue
    }

    const source = path.join(filesRoot, file.from)
    const relativePath = render(file.to, answers, `destination of ${ file.from }`)

    if (BINARY_EXTENSIONS.has(path.extname(file.from).toLowerCase())) {
      rendered.push({ relativePath, contents: fs.readFileSync(source) })

      continue
    }

    const contents = render(fs.readFileSync(source, 'utf-8'), answers, `${ template.manifest.id }/files/${ file.from }`)

    rendered.push({ relativePath, contents: Buffer.from(contents, 'utf-8') })
  }

  return rendered
}

/**
 * Write a rendered template into `services/<serviceId>/` under `projectRoot`.
 *
 * Refuses an existing service directory outright — overwriting one would silently discard work, and the
 * service id is frozen once deployed, so a collision is far more likely to be a mistake than an intent.
 */
export function scaffoldService(
  template: Template,
  answers: Answers,
  serviceId: string,
  projectRoot: string = process.cwd()
): ScaffoldResult {
  const files = renderTemplate(template, answers)

  const servicesRoot = servicesDirectory(projectRoot)
  const serviceDirectory = resolveServiceDirectory(serviceId, projectRoot)

  // The id prompt already refuses a taken id; this is the last line of defence for `--id` on a racing or
  // scripted run, and it keeps `scaffoldService` safe to call on its own.
  if (fs.existsSync(serviceDirectory)) {
    throw new Error(`services/${ serviceId } already exists — pick another service id, or remove it first.`)
  }

  // Second containment check, now against the real destination. The manifest was validated at load time;
  // this catches a `to` path made unsafe by interpolation.
  for (const file of files) {
    const target = path.resolve(serviceDirectory, file.relativePath)

    if (target !== serviceDirectory && !target.startsWith(serviceDirectory + path.sep)) {
      throw new Error(`Template "${ template.manifest.id }" resolved a path outside the service folder: ${ file.relativePath }`)
    }
  }

  const createdServicesDirectory = !fs.existsSync(servicesRoot)

  for (const file of files) {
    const target = path.join(serviceDirectory, file.relativePath)

    fs.mkdirSync(path.dirname(target), { recursive: true })
    fs.writeFileSync(target, file.contents)
  }

  return { serviceDirectory, files, createdServicesDirectory }
}
